import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const AuthenticatedNavBar = ({ user, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Generate',
      path: '/news-generation',
      icon: 'PenTool',
      tooltip: 'AI-powered news article generation'
    },
    {
      label: 'Verify',
      path: '/fact-checking',
      icon: 'CheckCircle',
      tooltip: 'Fact verification and credibility analysis'
    },
    {
      label: 'Analyze',
      path: '/bias-detection',
      icon: 'Search',
      tooltip: 'Content bias detection and objectivity assessment'
    },
    {
      label: 'Profile',
      path: '/user-profile',
      icon: 'User',
      tooltip: 'Account settings and preferences'
    }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-border shadow-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/news-generation" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <Icon name="Newspaper" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-semibold text-foreground">
                News Mind AI
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-250 flex items-center space-x-2 group relative ${
                  isActivePath(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-soft'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                title={item?.tooltip}
              >
                <Icon 
                  name={item?.icon} 
                  size={16} 
                  color={isActivePath(item?.path) ? 'currentColor' : 'currentColor'} 
                />
                <span>{item?.label}</span>
              </Link>
            ))}
          </div>

          {/* User Menu & Mobile Toggle */}
          <div className="flex items-center space-x-4">
            {/* User Info (Desktop) */}
            <div className="hidden md:flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {user?.name || 'Journalist'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {user?.role || 'Reporter'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onLogout}
                iconName="LogOut"
                iconPosition="left"
                iconSize={16}
                className="text-muted-foreground hover:text-foreground"
              >
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              className="md:hidden"
              iconName={isMobileMenuOpen ? 'X' : 'Menu'}
              iconSize={20}
            />
          </div>
        </div>
      </div>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeMobileMenu} />
          <div className="relative bg-white border-b border-border shadow-elevated">
            <div className="px-4 py-6 space-y-4">
              {/* Mobile Navigation Items */}
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-250 ${
                    isActivePath(item?.path)
                      ? 'bg-primary text-primary-foreground shadow-soft'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    color="currentColor" 
                  />
                  <div>
                    <span className="block">{item?.label}</span>
                    <span className="text-xs opacity-75">{item?.tooltip}</span>
                  </div>
                </Link>
              ))}

              {/* Mobile User Info & Logout */}
              <div className="border-t border-border pt-4 mt-6">
                <div className="px-4 py-2 mb-3">
                  <p className="text-base font-medium text-foreground">
                    {user?.name || 'Journalist'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.role || 'Reporter'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    onLogout();
                    closeMobileMenu();
                  }}
                  iconName="LogOut"
                  iconPosition="left"
                  iconSize={18}
                  className="w-full justify-start px-4 text-muted-foreground hover:text-foreground"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default AuthenticatedNavBar;
